import * as React from "react"
import { cn } from "@/lib/utils"

// Base Card components (shadcn style)
const BaseCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow",
      className
    )}
    {...props}
  />
))
BaseCard.displayName = "BaseCard"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

// Enhanced Card with hoverable support (backward compatible)
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  hoverable?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, hoverable = false, onClick, children, ...props }, ref) => {
    const hoverClasses = hoverable
      ? 'cursor-pointer hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all'
      : ''

    return (
      <BaseCard
        ref={ref}
        className={cn("p-4", hoverClasses, className)}
        onClick={onClick}
        {...props}
      >
        {children}
      </BaseCard>
    )
  }
)
Card.displayName = "Card"

// CardImage component (backward compatible)
interface CardImageProps {
  src: string | null
  alt: string
  className?: string
  fit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none'
}

const fitClass: Record<string, string> = {
  cover: 'object-cover',
  contain: 'object-contain',
  fill: 'object-fill',
  'scale-down': 'object-scale-down',
  none: 'object-none',
}

function CardImage({ src, alt, className = '', fit = 'cover' }: CardImageProps) {
  if (!src) {
    return (
      <div
        className={cn(
          "w-full aspect-video bg-secondary rounded-lg flex items-center justify-center",
          className
        )}
      >
        <svg
          className="w-12 h-12 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    )
  }

  return (
    <img
      src={`/${src}`}
      alt={alt}
      className={cn("w-full aspect-video rounded-lg", fitClass[fit], className)}
    />
  )
}

export { Card, CardImage, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
